/**
 * useSiteMap Hook
 *
 * Composes the site list and polled health into map entries, and reloads the site list
 * when the policy says so: when health lists sites the list doesn't have (throttled), or
 * when the tab becomes visible with a stale list.
 */
import { useEffect, useMemo, useRef } from 'react';
import { useSiteMapSites } from './useSiteMapSites';
import { useSiteMapHealth } from './useSiteMapHealth';
import { findUnmatchedHealth, joinSiteMap } from '../utils/siteMapJoin';
import { shouldReloadSites } from '../utils/sitesReloadPolicy';
import type { SiteMapEntry } from '../models/siteMapModels';
import type { HealthState, SitesState } from './siteMapState';

export interface SiteMapView {
  readonly sites: SitesState;
  readonly health: HealthState;
  readonly entries: readonly SiteMapEntry[];
  readonly retrySites: () => void;
}

export function useSiteMap(): SiteMapView {
  const sites = useSiteMapSites();
  const health = useSiteMapHealth();

  // Latest site list state for event handlers and effects keyed on other values
  const sitesRef = useRef(sites);
  sitesRef.current = sites;

  // Each new health result is checked against the list it is shown with
  useEffect(() => {
    const current = sitesRef.current;
    if (!current.data || !health.data) return;
    if (findUnmatchedHealth(current.data, health.data).length === 0) return;
    if (
      shouldReloadSites({
        trigger: 'unmatched-health',
        now: Date.now(),
        loadedAt: current.loadedAt,
        lastRequestedAt: current.lastRequestedAt,
      })
    ) {
      current.reload();
    }
  }, [health.data]);

  useEffect(() => {
    const onVisibilityChange = (): void => {
      if (document.hidden) return;
      const current = sitesRef.current;
      if (
        shouldReloadSites({
          trigger: 'visible',
          now: Date.now(),
          loadedAt: current.loadedAt,
          lastRequestedAt: current.lastRequestedAt,
        })
      ) {
        current.reload();
      }
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const entries = useMemo(
    () => (sites.data ? joinSiteMap(sites.data, health.data) : []),
    [sites.data, health.data]
  );

  return { sites, health, entries, retrySites: sites.reload };
}
